import { Avatar, Box, Container, Grid2 as Grid, Link, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useHead } from '@unhead/react';
import { useEffect, useMemo, useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { MdFormatQuote, MdStar } from 'react-icons/md';
import {
    GoogleReview,
    GoogleReviewsResult,
    fetchGoogleReviews,
    qualifyingReviews,
} from '../../utils/functions/googleReviews';
import { CuratedReview, curatedReviews } from '../../utils/stores/curatedReviews';
import { SITE_NAME, SITE_URL } from '../coreLayout/Seo';

const STAR_COLOR = '#fbbc04';

/** How many review cards to show. */
const DISPLAY_COUNT = 3;
/** Truncate long review text to keep cards a consistent height. */
const MAX_CARD_LENGTH = 280;

interface DisplayReview {
    author: string;
    rating: number;
    text: string;
    date: string;
    authorPhoto: string;
    sourceUrl: string;
}

function truncate(text: string, max = MAX_CARD_LENGTH): string {
    const trimmed = text.trim();
    if (trimmed.length <= max) return trimmed;
    const slice = trimmed.slice(0, max);
    const lastSpace = slice.lastIndexOf(' ');
    return `${slice.slice(0, lastSpace > 0 ? lastSpace : max).trimEnd()}…`;
}

const liveToDisplay = (r: GoogleReview): DisplayReview => ({
    author: r.authorName,
    rating: r.rating,
    text: r.text,
    date: r.relativeTime,
    authorPhoto: r.authorPhoto,
    sourceUrl: r.authorUri,
});

const curatedToDisplay = (c: CuratedReview): DisplayReview => ({
    author: c.author,
    rating: c.rating,
    text: c.text,
    date: c.date,
    authorPhoto: c.authorPhoto ?? '',
    sourceUrl: c.sourceUrl ?? '',
});

/**
 * Most recent qualifying live reviews first; top up with curated reviews when
 * there aren't enough fresh ones that meet the criteria.
 */
function composeReviews(live: GoogleReviewsResult | null): DisplayReview[] {
    const result = qualifyingReviews(live).slice(0, DISPLAY_COUNT).map(liveToDisplay);
    for (const curated of curatedReviews) {
        if (result.length >= DISPLAY_COUNT) break;
        if (result.some((r) => r.author === curated.author)) continue;
        result.push(curatedToDisplay(curated));
    }
    return result.slice(0, DISPLAY_COUNT);
}

const socialProofSx = {
    section: (theme: Theme) => ({
        width: '100%',
        background: `-webkit-linear-gradient(${theme.palette.background.subtle}, rgba(255, 255, 255, 1))`,
    }),
    heading: {
        textAlign: 'center',
        paddingTop: { xs: '40px', sm: '64px' },
    },
    aggregate: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        margin: '20px auto 0',
        padding: '12px 22px',
        width: 'fit-content',
        maxWidth: '100%',
        background: '#fff',
        borderRadius: '999px',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
    },
    grid: {
        justifyContent: 'center',
        paddingTop: { xs: '24px', sm: '40px' },
        paddingBottom: { xs: '36px', sm: '56px' },
    },
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.07)',
        border: '1px solid rgba(15, 23, 42, 0.05)',
        position: 'relative',
    },
    quoteMark: {
        position: 'absolute',
        top: '14px',
        right: '16px',
        color: 'primary.light',
        opacity: 0.6,
        fontSize: '2.2rem',
    },
    reviewText: {
        color: 'text.primary',
        fontSize: '1rem',
        lineHeight: 1.55,
        marginTop: '12px',
        marginBottom: '20px',
        flexGrow: 1,
    },
    footerRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
} as const;

const eyebrowSx = {
    color: 'primary.main',
    letterSpacing: '0.2em',
    fontWeight: 800,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    mb: 1.5,
} as const;

const headingSx = {
    fontSize: { xs: 30, md: 40 },
    fontWeight: 700,
    color: 'text.primary',
    lineHeight: 1.12,
    letterSpacing: '-1px',
    m: 0,
} as const;

function Stars({ value, size = 18 }: { value: number; size?: number }) {
    const full = Math.round(value);
    return (
        <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }} aria-label={`${value} out of 5 stars`}>
            {Array.from({ length: 5 }, (_, i) => i).map((starIndex) => (
                <MdStar
                    key={`star-${starIndex}`}
                    size={size}
                    color={starIndex < full ? STAR_COLOR : 'rgba(15, 23, 42, 0.18)'}
                />
            ))}
        </Box>
    );
}

export default function SocialProof() {
    const [live, setLive] = useState<GoogleReviewsResult | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        fetchGoogleReviews(controller.signal).then(setLive);
        return () => controller.abort();
    }, []);

    const reviews = useMemo(() => composeReviews(live), [live]);

    // Expose live Google ratings to search/AI crawlers as schema.org data. Only
    // emitted from real fetched data so the numbers are always accurate.
    const ratingSchema = useMemo(() => {
        if (!live || live.total <= 0) return null;
        const topReviews = qualifyingReviews(live).slice(0, 3);
        return {
            '@context': 'https://schema.org',
            '@type': 'TravelAgency',
            '@id': `${SITE_URL}/#organization`,
            name: SITE_NAME,
            url: SITE_URL,
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: live.rating.toFixed(1),
                reviewCount: live.total,
                bestRating: 5,
                worstRating: 1,
            },
            ...(topReviews.length > 0 && {
                review: topReviews.map((r) => ({
                    '@type': 'Review',
                    author: { '@type': 'Person', name: r.authorName },
                    reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
                    ...(r.publishTime && { datePublished: r.publishTime }),
                    reviewBody: r.text,
                })),
            }),
        };
    }, [live]);

    useHead({
        script: ratingSchema ? [{ type: 'application/ld+json', innerHTML: JSON.stringify(ratingSchema) }] : [],
    });

    if (reviews.length === 0) return null;

    const ratingLabel = live ? live.rating.toFixed(1) : '5.0';

    return (
        <Box sx={socialProofSx.section}>
            <Container maxWidth="lg">
                <Box sx={socialProofSx.heading}>
                    <Typography component="p" sx={eyebrowSx}>
                        Loved by travellers
                    </Typography>
                    <Typography component="h2" sx={headingSx}>
                        What our guests say
                    </Typography>

                    <Box sx={socialProofSx.aggregate}>
                        <FcGoogle size={24} />
                        <Typography
                            component="span"
                            sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'text.primary' }}
                        >
                            {ratingLabel}
                        </Typography>
                        <Stars value={live ? live.rating : 5} size={20} />
                        {live && live.total > 0 && (
                            <Typography component="span" sx={{ color: 'text.soft', fontWeight: 500 }}>
                                from {live.total.toLocaleString()} Google reviews
                            </Typography>
                        )}
                        {live?.googleMapsUri && (
                            <Link
                                href={live.googleMapsUri}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}
                            >
                                Read on Google
                            </Link>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={3} sx={socialProofSx.grid}>
                    {reviews.map((review, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`review-${i + 1}`}>
                            <Box sx={socialProofSx.card}>
                                <Box component={MdFormatQuote} sx={socialProofSx.quoteMark} />
                                <Stars value={review.rating} />
                                <Typography sx={socialProofSx.reviewText}>{truncate(review.text)}</Typography>
                                <Box sx={socialProofSx.footerRow}>
                                    <Avatar
                                        src={review.authorPhoto || undefined}
                                        alt={review.author}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: 'primary.light',
                                            color: 'primary.dark',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {review.author.charAt(0)}
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem' }}
                                        >
                                            {review.author}
                                        </Typography>
                                        <Typography sx={{ color: 'text.soft', fontSize: '0.82rem' }}>
                                            {review.date} · Google
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}
